import { PcbNodeTypes } from "@buildwithflux/models";
import { areWeTestingWithJest, enforceGridPrecisionInMeters } from "@buildwithflux/shared";
import { MissingChildUidError } from "../../../flux_error";
import { getAncestorUidByType } from "../../../nodes";
import { getTransformBakedRules, hasAbsTransformChanged, updateBakedRuleValues } from "../helpers";
import { PcbAutoInitialPositionCalcs } from "./PcbAutoInitialPositionCalcs";
export class PcbAutoPositionUpdater {
    logger;
    elements;
    treeManager;
    constructor(elements, treeManager, logger) {
        this.logger = logger;
        this.elements = elements;
        this.treeManager = treeManager;
    }
    /**
     * Auto-position all nodes within the given layouts by recursive descent.
     */
    autoPositionFromTop(topLevelLayouts) {
        const autoPositioner = new PcbAutoInitialPositionCalcs(this.elements);
        const pcbLayoutNodes = this.treeManager.treeNodes; // TODO: fix baked node cast
        for (const rootLayout of topLevelLayouts) {
            const computedNodePositions = autoPositioner.autoInitialPositionLayout(pcbLayoutNodes, rootLayout, this.logger);
            if (computedNodePositions) {
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                computedNodePositions.forEach(this.updatePcbLayoutNodePosition.bind(this));
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                computedNodePositions.forEach((nodePosition) => this.recalculateRootLocationsForSubtree(pcbLayoutNodes, nodePosition.uid));
            }
        }
    }
    updatePcbLayoutNodePosition(nodePosition) {
        // TODO: [NodeType] - using `any` for now, should make TreeManager generic for node types
        // and infer type for us
        this.treeManager.updatePcbLayoutNode(nodePosition.uid, (nodeDraft) => {
            if (nodeDraft?.bakedRules && "position" in nodeDraft.bakedRules) {
                // HACK: make unit tests pass after correctly fixing their status
                // if there is a real bug here we'll find out how frequently it occurs in prod
                if (Number.isNaN(nodePosition.position.x) || Number.isNaN(nodePosition.position.y)) {
                    if (!areWeTestingWithJest()) {
                        this.logger.captureError(new Error("Auto-positioning somehow returned NaN. Skipping."));
                    }
                    return;
                }
                nodeDraft.bakedRules.position.x = enforceGridPrecisionInMeters(nodePosition.position.x);
                nodeDraft.bakedRules.position.y = enforceGridPrecisionInMeters(nodePosition.position.y);
            }
        });
    }
    recalculateRootLocationsForSubtree(pcbLayoutNodes, nodeUid) {
        const node = this.treeManager.treeNodes[nodeUid];
        if (!node.bakedRules) {
            return;
        }
        const parentNode = this.treeManager.treeNodes[node.parentUid];
        const topLevelLayoutUid = getAncestorUidByType(this.treeManager.treeNodes, nodeUid, PcbNodeTypes.layout, true);
        const topLevelFootprintUid = getAncestorUidByType(this.treeManager.treeNodes, nodeUid, PcbNodeTypes.footprint, true);
        const rootFootprintOrLayoutUid = topLevelLayoutUid ?? topLevelFootprintUid;
        const parentLayout = rootFootprintOrLayoutUid ? this.treeManager.treeNodes[rootFootprintOrLayoutUid] : null;
        const absoluteTransforms = getTransformBakedRules(node.bakedRules, parentNode, parentLayout);
        if (hasAbsTransformChanged(absoluteTransforms, node)) {
            updateBakedRuleValues(node, { ...node.bakedRules, ...absoluteTransforms }, this.treeManager);
            // NOTE: We play safe here to fallback childUids to empty, but want to know
            // that childUids is missing, so that we can investigate and take actions from here
            const pcbLayoutNode = pcbLayoutNodes[nodeUid];
            const childUids = pcbLayoutNode?.childUids;
            if (!childUids) {
                this.logger.captureError(new MissingChildUidError(`node ${nodeUid} with type ${pcbLayoutNode?.type} in recalculateRootLocationsForSubtree`));
            }
            else {
                // Also, since `PcbAutoPositionCalcs` is used in this class and there is another `.getChildrenUids` with in it, it'd be better
                // to get childrenUids once and pass down?
                for (const childUid of childUids) {
                    this.recalculateRootLocationsForSubtree(pcbLayoutNodes, childUid);
                }
            }
        }
    }
}
//# sourceMappingURL=PcbAutoInitialPositionUpdater.js.map